// /frontend/src/pages/admin/AdminSalesReports.jsx
import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { 
  FaDownload, 
  FaCalendarAlt, 
  FaChartLine, 
  FaChartBar, 
  FaChartPie,
  FaChartArea,
  FaFilter
} from 'react-icons/fa';
import { salesService } from '../../services/salesService';

const AdminSalesReports = () => {
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Report filters
  const [dateRange, setDateRange] = useState('last30days');
  const [reportType, setReportType] = useState('sales');
  
  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];
  
  const reportTypeOptions = [
    { value: 'sales', label: 'Sales Overview', icon: <FaChartLine /> },
    { value: 'products', label: 'Product Performance', icon: <FaChartBar /> },
    { value: 'categories', label: 'Category Analysis', icon: <FaChartPie /> },
    { value: 'customers', label: 'Customer Insights', icon: <FaChartArea /> }
  ];
  
  // Custom date range
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Fetch sales data
  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        setLoading(true);
        // Replace with your actual API call when implemented
        const data = await salesService.getSalesReport({
          dateRange,
          reportType,
          startDate: dateRange === 'custom' ? startDate : undefined,
          endDate: dateRange === 'custom' ? endDate : undefined
        });
        
        setSalesData(data);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to fetch sales data');
        setLoading(false);
      }
    };
    
    fetchSalesData();
  }, [dateRange, reportType, startDate, endDate]);
  
  // Handle export report
  const handleExportReport = async (format) => {
    try {
      await salesService.exportReport({
        dateRange,
        reportType,
        startDate: dateRange === 'custom' ? startDate : undefined,
        endDate: dateRange === 'custom' ? endDate : undefined,
        format
      });
      
      alert(`Report exported successfully in ${format.toUpperCase()} format`);
    } catch (err) {
      alert(`Failed to export report: ${err.message}`);
    }
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-800"></div>
        </div>
      </AdminLayout>
    );
  }
  
  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md mb-6">
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Sales Reports</h1>
          <p className="text-gray-600 mt-1">Analyze your store's performance with detailed insights</p>
        </div>
        <div className="flex space-x-2">
          <div className="relative group">
            <button className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700">
              <FaDownload className="mr-2" />
              Export
            </button>
            
            {/* Export format dropdown */}
            <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10 hidden group-hover:block">
              <div className="py-1">
                <button
                  onClick={() => handleExportReport('csv')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Export as CSV
                </button>
                <button
                  onClick={() => handleExportReport('excel')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Export as Excel
                </button>
                <button
                  onClick={() => handleExportReport('pdf')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Export as PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filter section */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Date Range */}
          <div>
            <label htmlFor="dateRange" className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <div className="relative">
              <select
                id="dateRange"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {dateRangeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <FaCalendarAlt className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
          
          {/* Custom Date Range */}
          {dateRange === 'custom' && (
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
          )}
          
          {/* Report Type (if not custom date range) */}
          {dateRange !== 'custom' && (
            <div>
              <label htmlFor="reportType" className="block text-sm font-medium text-gray-700 mb-1">
                Report Type
              </label>
              <div className="relative">
                <select
                  id="reportType"
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {reportTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <FaFilter className="absolute left-3 top-3 text-gray-400" />
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-gray-500">Total Sales</h3>
            <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
              <FaChartLine className="text-teal-600" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900">
            ${salesData?.totalSales.toFixed(2) || '0.00'}
          </div>
          {salesData?.salesGrowth && (
            <div className={`mt-2 text-sm ${
              salesData.salesGrowth >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {salesData.salesGrowth >= 0 ? '+' : ''}{salesData.salesGrowth}% from previous period
            </div>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-gray-500">Orders</h3>
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FaChartBar className="text-blue-600" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900">
            {salesData?.totalOrders || 0}
          </div>
          {salesData?.ordersGrowth && (
            <div className={`mt-2 text-sm ${
              salesData.ordersGrowth >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {salesData.ordersGrowth >= 0 ? '+' : ''}{salesData.ordersGrowth}% from previous period
            </div>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-gray-500">Average Order Value</h3>
            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
              <FaChartPie className="text-amber-600" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900">
            ${salesData?.averageOrderValue.toFixed(2) || '0.00'}
          </div>
          {salesData?.aovGrowth && (
            <div className={`mt-2 text-sm ${
              salesData.aovGrowth >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {salesData.aovGrowth >= 0 ? '+' : ''}{salesData.aovGrowth}% from previous period
            </div>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-gray-500">Conversion Rate</h3>
            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <FaChartArea className="text-emerald-600" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900">
            {salesData?.conversionRate.toFixed(2) || '0.00'}%
          </div>
          {salesData?.conversionGrowth && (
            <div className={`mt-2 text-sm ${
              salesData.conversionGrowth >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {salesData.conversionGrowth >= 0 ? '+' : ''}{salesData.conversionGrowth}% from previous period
            </div>
          )}
        </div>
      </div>
      
      {/* Report Content Based on Type */}
      {reportType === 'sales' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Sales Over Time */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden lg:col-span-2">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-800">Sales Over Time</h2>
            </div>
            <div className="p-6">
              {/* Here you would normally include a chart component */}
              <div className="h-80 flex items-center justify-center bg-gray-100 rounded-lg">
                {/* Placeholder for actual chart */}
                <p className="text-gray-500">Sales chart will be displayed here</p>
              </div>
            </div>
          </div>
          
          {/* Sales by Payment Method */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-800">Sales by Payment Method</h2>
            </div>
            <div className="p-6">
              {/* Here you would normally include a chart component */}
              <div className="h-64 flex items-center justify-center bg-gray-100 rounded-lg">
                {/* Placeholder for actual chart */}
                <p className="text-gray-500">Payment method chart will be displayed here</p>
              </div>
            </div>
          </div>
          
          {/* Sales by Device */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-800">Sales by Device</h2>
            </div>
            <div className="p-6">
              {/* Here you would normally include a chart component */}
              <div className="h-64 flex items-center justify-center bg-gray-100 rounded-lg">
                {/* Placeholder for actual chart */}
                <p className="text-gray-500">Device breakdown chart will be displayed here</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {reportType === 'products' && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-800">Top Products</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Units Sold
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Profit
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversion Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {salesData?.topProducts?.map((product, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <img 
                            className="h-10 w-10 rounded-md object-cover" 
                            src={product.image || 'https://via.placeholder.com/150'} 
                            alt={product.name}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-500">SKU: {product.sku}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{product.unitsSold}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">${product.revenue.toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">${product.profit.toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{product.conversionRate}%</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {reportType === 'categories' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Category Sales Distribution */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-800">Category Sales Distribution</h2>
            </div>
            <div className="p-6">
              {/* Here you would normally include a chart component */}
              <div className="h-80 flex items-center justify-center bg-gray-100 rounded-lg">
                {/* Placeholder for actual chart */}
                <p className="text-gray-500">Category sales pie chart will be displayed here</p>
              </div>
            </div>
          </div>
          
          {/* Category Performance */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-800">Category Performance</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Revenue
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Orders
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg. Value
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {salesData?.categoryPerformance?.map((category, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {category.name.charAt(0).toUpperCase() + category.name.slice(1).replace('-', ' ')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">${category.revenue.toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{category.orders}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">${category.averageValue.toFixed(2)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {reportType === 'customers' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Customer Acquisition */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-800">Customer Acquisition</h2>
            </div>
            <div className="p-6">
              {/* Here you would normally include a chart component */}
              <div className="h-80 flex items-center justify-center bg-gray-100 rounded-lg">
                {/* Placeholder for actual chart */}
                <p className="text-gray-500">Customer acquisition chart will be displayed here</p>
              </div>
            </div>
          </div>
          
          {/* Top Customers */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-800">Top Customers</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Orders
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Spent
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg. Order Value
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {salesData?.topCustomers?.map((customer, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                        <div className="text-sm text-gray-500">{customer.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{customer.orders}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">${customer.totalSpent.toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">${customer.averageOrderValue.toFixed(2)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSalesReports;